import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import axios from 'axios';
import { ChevronRight, ArrowLeft, Package, Folder } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default placeholder images for categories
const defaultImages = {
  'exterior': 'https://customer-assets.emergentagent.com/job_sunprotect/artifacts/mx206kbm_in%C5%A1tal%C3%A1cia%20tepelnoizola%C4%8Dnej%20f%C3%B3lie-2.webp',
  'interior': 'https://customer-assets.emergentagent.com/job_sunprotect/artifacts/96b1auzw_4.NSN70_1000x1000.webp',
  'architectural': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
  'automotive': 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&q=80',
  'tools': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&q=80',
  'materials': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
  'default': 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80'
};

const CatalogCategories = () => {
  const { t, i18n } = useTranslation();
  const { categoryId } = useParams();
  const navigate = useNavigate();
  
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [childCategories, setChildCategories] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (categories.length > 0) {
      updateView();
    }
  }, [categoryId, categories]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateView = () => {
    if (categoryId) {
      const current = categories.find(c => c.id === categoryId);
      setCurrentCategory(current);
      
      const children = categories.filter(c => c.parent_id === categoryId);
      setChildCategories(children);
      
      const crumbs = [];
      let cat = current;
      while (cat) {
        crumbs.unshift(cat);
        cat = categories.find(c => c.id === cat.parent_id);
      }
      setBreadcrumbs(crumbs);
    } else {
      setCurrentCategory(null);
      const rootCategories = categories.filter(c => 
        c.parent_id === null || c.parent_id === undefined || c.parent_id === ''
      );
      setChildCategories(rootCategories);
      setBreadcrumbs([]);
    }
  };

  const getTranslation = (obj, field) => {
    return obj?.translations?.[getUiLang(i18n)]?.[field] || 
           obj?.translations?.['sk']?.[field] || 
           obj?.translations?.['en']?.[field] || '';
  };

  const hasChildren = (catId) => {
    return categories.some(c => c.parent_id === catId);
  };

  const handleCategoryClick = (category) => {
    if (hasChildren(category.id)) {
      navigate(`/catalog/category/${category.id}`);
    } else {
      navigate(`/catalog?category=${category.id}`);
    }
  };

  // Get category image - from database or default based on name
  const getCategoryImage = (category) => {
    // First check if category has custom image
    if (category.image_url) {
      return category.image_url;
    }
    
    // Fallback to defaults based on name/slug
    const name = getTranslation(category, 'name').toLowerCase();
    const slug = (category.slug || '').toLowerCase();
    
    if (slug.includes('exterier') || slug.includes('exterior') || name.includes('exteriér') || name.includes('exterior')) {
      return defaultImages.exterior;
    }
    if (slug.includes('interier') || slug.includes('interior') || name.includes('interiér') || name.includes('interior')) {
      return defaultImages.interior;
    }
    if (slug.includes('architekt') || name.includes('architekt')) {
      return defaultImages.architectural;
    }
    if (slug.includes('auto') || name.includes('auto') || name.includes('automobil')) {
      return defaultImages.automotive;
    }
    if (slug.includes('nastroj') || name.includes('nástroj') || name.includes('tool')) {
      return defaultImages.tools;
    }
    if (slug.includes('montaz') || name.includes('montáž') || name.includes('material')) {
      return defaultImages.materials;
    }
    
    return defaultImages.default;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // Determine grid columns based on number of items
  const getGridCols = (count) => {
    if (count === 1) return 'grid-cols-1 max-w-2xl mx-auto';
    if (count === 2) return 'grid-cols-1 md:grid-cols-2 max-w-5xl mx-auto';
    if (count === 3) return 'grid-cols-1 md:grid-cols-3';
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
  };

  // Determine card height based on level
  const getCardHeight = () => {
    if (!currentCategory) return 'h-[280px]'; // Root level
    const children = categories.filter(c => c.parent_id === categoryId);
    if (children.length <= 2) return 'h-[350px]'; // 2 items - bigger cards
    return 'h-[280px]';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-12">
        <div className="container mx-auto px-4">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-white/80 mb-4 text-sm">
            <Link to="/catalog/categories" className="hover:text-white">
              {t('catalog.all_categories')}
            </Link>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.id}>
                <ChevronRight className="h-4 w-4" />
                {index === breadcrumbs.length - 1 ? (
                  <span className="text-white font-medium">
                    {getTranslation(crumb, 'name')}
                  </span>
                ) : (
                  <Link 
                    to={`/catalog/category/${crumb.id}`}
                    className="hover:text-white"
                  >
                    {getTranslation(crumb, 'name')}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </nav>

          <h1 className="text-4xl font-bold" data-testid="catalog-categories-title">
            {currentCategory 
              ? getTranslation(currentCategory, 'name')
              : t('catalog.all_categories')
            }
          </h1>
          
          {currentCategory && getTranslation(currentCategory, 'description') && (
            <p className="mt-2 text-white/90 max-w-2xl">
              {getTranslation(currentCategory, 'description')}
            </p>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        {currentCategory && (
          <button
            onClick={() => {
              if (currentCategory.parent_id) {
                navigate(`/catalog/category/${currentCategory.parent_id}`);
              } else {
                navigate('/catalog/categories');
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-orange-500 mb-6 transition"
          >
            <ArrowLeft className="h-5 w-5" />
            {t('common.back')}
          </button>
        )}

        {/* Category Cards Grid */}
        {childCategories.length > 0 ? (
          <div className={`grid gap-6 ${getGridCols(childCategories.length)}`}>
            {childCategories.map((category) => {
              const childCount = categories.filter(c => c.parent_id === category.id).length;
              const hasSubcategories = childCount > 0;
              const imageUrl = getCategoryImage(category);
              
              return (
                <div
                  key={category.id}
                  onClick={() => handleCategoryClick(category)}
                  className="group cursor-pointer"
                  data-testid={`category-card-${category.id}`}
                >
                  <div className={`
                    relative overflow-hidden rounded-2xl shadow-lg 
                    ${getCardHeight()}
                    transform transition-all duration-500 ease-out
                    hover:scale-[1.02] hover:shadow-2xl
                  `}>
                    {/* Background Image */}
                    <div 
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                      style={{ backgroundImage: `url(${imageUrl})` }}
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-orange-500/0 group-hover:bg-orange-500/20 transition-colors duration-300" />
                    
                    {/* Content */}
                    <div className="absolute inset-0 flex flex-col justify-end p-6">
                      <h3 className="text-white text-2xl md:text-3xl font-bold mb-2 transform transition-transform duration-300 group-hover:translate-x-2">
                        {getTranslation(category, 'name')}
                      </h3>
                      
                      {getTranslation(category, 'description') && (
                        <p className="text-white/80 text-sm mb-3 line-clamp-2">
                          {getTranslation(category, 'description')}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between">
                        {hasSubcategories ? (
                          <p className="text-white/80 text-sm flex items-center gap-2">
                            <Folder className="h-4 w-4" />
                            {childCount} {t('catalog.subcategories')}
                          </p>
                        ) : (
                          <p className="text-white/80 text-sm flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            {t('catalog.view_products')}
                          </p>
                        )}
                        
                        {/* Arrow Button */}
                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center transform transition-all duration-300 group-hover:bg-orange-500 group-hover:translate-x-1">
                          <ChevronRight className="h-5 w-5 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // No subcategories - show link to products
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold mb-4">
              {t('catalog.no_subcategories')}
            </h3>
            <Link
              to={`/catalog?category=${categoryId}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition font-semibold"
            >
              <Package className="h-5 w-5" />
              {t('catalog.view_products')}
            </Link>
          </div>
        )}

        {/* Quick link to all products */}
        {!currentCategory && (
          <div className="mt-12 text-center">
            <Link
              to="/catalog"
              className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium"
            >
              {t('catalog.view_all_products')}
              <ChevronRight className="h-5 w-5" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CatalogCategories;
